import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import polyUtil from "polyline-encoded";
import axios from "axios";
import debounce from "lodash.debounce";
import { navigateToDetail } from "../../SportRoutes/Utils/utils";
import Types from "../../../constants/routeTypes";
import {
  DETAIL_MAP_LOADING_KEYS,
  useDetailMapLoading,
} from "./DetailMapLoadingContext";

const API_URL = "/umbraco/api/routeTest/get";
const OFFROAD_BIKE_ROUTE_OWNER_ID = 30581;
const SURROUNDING_ROUTE_FALLBACK_COLOR = "#888888";
const SURROUNDING_ROUTES_LIMIT = 100;
const SURROUNDING_ROUTE_OPACITY = 0.35;
const SURROUNDING_ROUTE_HOVER_OPACITY = 0.7;
const SURROUNDING_ROUTE_PANE = "surrounding-routes-pane";
const SURROUNDING_ROUTE_HOVER_PANE = "surrounding-routes-hover-pane";

const getSurroundingRouteColor = (routeDetail) => {
  return (
    routeDetail?.configuration?.line?.normal?.standard?.color ||
    routeDetail?.configuration?.line?.normal?.standard?.fill?.color ||
    SURROUNDING_ROUTE_FALLBACK_COLOR
  );
};

const SurroundingRoutePolyline = ({
  isHovered,
  onMouseOut,
  onMouseOver,
  route,
  redirectRoute,
}) => {
  const pane = isHovered
    ? SURROUNDING_ROUTE_HOVER_PANE
    : SURROUNDING_ROUTE_PANE;

  const pathOptions = {
    color: route.color,
    weight: isHovered ? 6 : 4,
    opacity: 1,
  };

  return (
    <Polyline
      pane={pane}
      positions={route.positions}
      pathOptions={pathOptions}
      eventHandlers={{
        mouseover: onMouseOver,
        mouseout: onMouseOut,
        click: () => navigateToDetail(route.id, globalThis, redirectRoute),
      }}
    >
      <Tooltip>
        {route.name}
        {route.distance ? ` — ${route.distance} km` : ""}
      </Tooltip>
    </Polyline>
  );
};

SurroundingRoutePolyline.propTypes = {
  isHovered: PropTypes.bool,
  onMouseOut: PropTypes.func.isRequired,
  onMouseOver: PropTypes.func.isRequired,
  route: PropTypes.shape({
    color: PropTypes.string.isRequired,
    distance: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    positions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  }).isRequired,
  redirectRoute: PropTypes.string.isRequired,
};

const SurroundingRoutes = ({ routeId, typeId, ownerId, redirectRoute }) => {
  const [hoveredRouteId, setHoveredRouteId] = useState(null);
  const [routes, setRoutes] = useState([]);
  const abortRef = useRef(null);
  const debouncedFetchRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const map = useMap();
  const { setLoadingState } = useDetailMapLoading();

  useEffect(() => {
    const surroundingRoutePane =
      map.getPane(SURROUNDING_ROUTE_PANE) || map.createPane(SURROUNDING_ROUTE_PANE);
    surroundingRoutePane.style.zIndex = "350";
    surroundingRoutePane.style.opacity = String(SURROUNDING_ROUTE_OPACITY);

    const surroundingRouteHoverPane =
      map.getPane(SURROUNDING_ROUTE_HOVER_PANE) ||
      map.createPane(SURROUNDING_ROUTE_HOVER_PANE);
    surroundingRouteHoverPane.style.zIndex = "360";
    surroundingRouteHoverPane.style.opacity = String(
      SURROUNDING_ROUTE_HOVER_OPACITY
    );
  }, [map]);

  const fetchSurroundingRoutes = useCallback(async () => {
    if (!routeId || !typeId) {
      setLoadingState(DETAIL_MAP_LOADING_KEYS.surroundingRoutes, false);
      return;
    }

    const resolvedOwnerId = Types.OffroadBikeRoutes.includes(typeId)
      ? OFFROAD_BIKE_ROUTE_OWNER_ID
      : ownerId;

    const fetchRequestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = fetchRequestId;
    setLoadingState(DETAIL_MAP_LOADING_KEYS.surroundingRoutes, true);

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      const bounds = map.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const searchFilter = {
        "type.id": [typeId],
        "permission.readable": true,
        "bounds.comparator": "geometry",
        language: "nl",
        "exclude.group.id": [55551],
        bounds: {
          min: { lon: sw.lng, lat: sw.lat },
          max: { lon: ne.lng, lat: ne.lat },
        },
      };

      if (resolvedOwnerId) {
        searchFilter["owner.id"] = [resolvedOwnerId];
      }

      const searchBody = {
        method: "searchAdvanced",
        params: [searchFilter, "score DESC", SURROUNDING_ROUTES_LIMIT, 0, {}],
      };

      const searchRes = await axios.post(API_URL, searchBody, { signal });
      const searchRoutes = (searchRes.data?.routes || [])
        .filter((r) => Math.round(r.id) !== Math.round(routeId))
        .slice(0, SURROUNDING_ROUTES_LIMIT);

      if (searchRoutes.length === 0) {
        setHoveredRouteId(null);
        setRoutes([]);
        return;
      }

      const routeIds = searchRoutes.map((r) => r.id);
      const fullBody = {
        method: "getFull",
        params: [routeIds, "nl"],
      };

      const fullRes = await axios.post(API_URL, fullBody, { signal });
      const fullData = fullRes.data || {};

      const processed = routeIds
        .map((id) => {
          const detail = fullData[id];
          if (!detail?.geometry?.google) return null;
          return {
            id: detail.id,
            name: detail.name?.nl || "",
            distance: detail.length
              ? (detail.length / 1000).toFixed(1)
              : null,
            positions: polyUtil.decode(detail.geometry.google, 5),
            color: getSurroundingRouteColor(detail),
          };
        })
        .filter(Boolean);

      setHoveredRouteId((currentHoveredRouteId) => {
        if (processed.some((route) => route.id === currentHoveredRouteId)) {
          return currentHoveredRouteId;
        }

        return null;
      });
      setRoutes(processed);
    } catch (err) {
      if (
        err.name === "AbortError" ||
        err.name === "CanceledError" ||
        axios.isCancel(err)
      ) {
        return;
      }

      setHoveredRouteId(null);
      setRoutes([]);
    } finally {
      if (fetchRequestId === fetchRequestIdRef.current) {
        setLoadingState(DETAIL_MAP_LOADING_KEYS.surroundingRoutes, false);
      }
    }
  }, [map, ownerId, routeId, setLoadingState, typeId]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchSurroundingRoutes, 500);
    debouncedFetchRef.current = debouncedFetch;

    const timer = setTimeout(fetchSurroundingRoutes, 100);

    return () => {
      fetchRequestIdRef.current += 1;
      setHoveredRouteId(null);
      setLoadingState(DETAIL_MAP_LOADING_KEYS.surroundingRoutes, false);
      clearTimeout(timer);
      debouncedFetch.cancel();
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchSurroundingRoutes, setLoadingState]);

  useMapEvents({
    moveend: () => {
      if (debouncedFetchRef.current) debouncedFetchRef.current();
    },
  });

  return (
    <>
      {routes
        .filter((route) => route.id !== hoveredRouteId)
        .map((route) => (
          <SurroundingRoutePolyline
            key={route.id}
            isHovered={false}
            onMouseOut={() => setHoveredRouteId(null)}
            onMouseOver={() => setHoveredRouteId(route.id)}
            route={route}
            redirectRoute={redirectRoute}
          />
        ))}

      {routes
        .filter((route) => route.id === hoveredRouteId)
        .map((route) => (
          <SurroundingRoutePolyline
            key={route.id}
            isHovered={true}
            onMouseOut={() => setHoveredRouteId(null)}
            onMouseOver={() => setHoveredRouteId(route.id)}
            route={route}
            redirectRoute={redirectRoute}
          />
        ))}
    </>
  );
};

SurroundingRoutes.propTypes = {
  ownerId: PropTypes.number,
  redirectRoute: PropTypes.string.isRequired,
  routeId: PropTypes.number.isRequired,
  typeId: PropTypes.number.isRequired,
};

export default SurroundingRoutes;
